import { useState } from 'react'
import { ref, getDownloadURL } from 'firebase/storage'
import { storage } from '../firebase/config'
import { HelpCircle, X, Loader } from 'lucide-react'

const PageHelpButton = ({ videoId, title, youtubeUrl }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [videoUrl, setVideoUrl] = useState('')

  const toggle = async () => {
    if (open) {
      setOpen(false)
      return
    }
    setError('')
    setOpen(true)

    // For YouTube embeds we don't need to fetch anything
    if (youtubeUrl) return

    if (videoUrl) return
    try {
      setLoading(true)
      const fileRef = ref(storage, `adminhelp/${videoId}.mp4`)
      const url = await getDownloadURL(fileRef)
      setVideoUrl(url)
    } catch (e) {
      console.error('Failed to load help video', e)
      setError('Unable to load help video. Please try again or contact support.')
    } finally {
      setLoading(false)
    }
  }

  const showStorageVideo = !youtubeUrl && videoUrl

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-purple-500 text-xs font-medium text-purple-100 hover:bg-purple-600/30 bg-purple-900/40"
      >
        <HelpCircle className="w-4 h-4" />
        <span>{open ? 'Hide help' : 'Show help'}</span>
      </button>

      {open && (
        <div className="mt-3 bg-gray-900 rounded-xl border border-purple-700/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-white" />
              <p className="text-xs sm:text-sm font-semibold text-white">
                {title || 'Quick tutorial for this page'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-white/80 hover:text-white hover:bg-white/10"
              aria-label="Close help"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-3 sm:p-4">
            {loading && !youtubeUrl && (
              <div className="flex items-center justify-center py-6 text-gray-300 text-sm">
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                <span>Loading help video…</span>
              </div>
            )}
            {!loading && error && (
              <p className="text-sm text-red-300">{error}</p>
            )}
            {!loading && !error && youtubeUrl && (
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                <iframe
                  className="w-full h-full"
                  src={youtubeUrl}
                  title={title || 'Help video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>
            )}
            {!loading && !error && showStorageVideo && (
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                <video
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  controls
                  preload="metadata"
                />
              </div>
            )}
            {!loading && !error && !youtubeUrl && !videoUrl && (
              <p className="text-sm text-gray-300">
                Help video will appear here once loaded.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PageHelpButton

